/**
 * The knowledge-base page's sections, and the deep link that opens one.
 *
 * Kept out of the page component so other screens (the dashboard readiness
 * card) can link straight at a section without importing a route component.
 */

export type KnowledgeBaseSection =
  | 'brand-identity'
  | 'voice'
  | 'products'
  | 'proof-points'
  | 'looks'
  | 'assets'
  | 'inspiration'
  | 'brand-memory'
  | 'integrations';

/**
 * Display order and the business-kind labels: words, then proof, then looks,
 * then where posts go out. A personal brand relabels some of these ("Offers",
 * "Credentials"); `brandKindCopy(kind).sections` has the per-kind label and
 * `KnowledgeBasePage` reads it. Keys never change.
 */
export const KNOWLEDGE_BASE_SECTIONS: readonly { key: KnowledgeBaseSection; label: string }[] = [
  { key: 'brand-identity', label: 'About your brand' },
  { key: 'voice', label: 'Example posts' },
  { key: 'products', label: 'Products' },
  { key: 'proof-points', label: 'Testimonials & results' },
  { key: 'looks', label: 'Visual style' },
  { key: 'assets', label: 'Images & logos' },
  { key: 'inspiration', label: 'Inspiration' },
  { key: 'brand-memory', label: 'Brand memory' },
  { key: 'integrations', label: 'Connected accounts' },
];

/**
 * Section shown when the URL names none, or names one that no longer exists
 * (an old `?section=calendar` or `?section=copy-check` link lands here).
 */
export const DEFAULT_KNOWLEDGE_BASE_SECTION: KnowledgeBaseSection = 'brand-identity';

/** Query param carrying the open section, so a section is linkable. */
export const KNOWLEDGE_BASE_SECTION_PARAM = 'section';

export function isKnowledgeBaseSection(value: string | null): value is KnowledgeBaseSection {
  return KNOWLEDGE_BASE_SECTIONS.some((entry) => entry.key === value);
}

/** Deep link to one section of `/knowledge-base`. */
export function knowledgeBaseSectionPath(section: KnowledgeBaseSection): string {
  return `/knowledge-base?${KNOWLEDGE_BASE_SECTION_PARAM}=${section}`;
}
